import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  crmsOptionGet,
  dailyReportGet,
  processOptionGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { didOptionGet } from '../../../store/slices/tenantSlices/incomingSlice';

import FormDateInput from '../../../components/form/FormDateInput';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import Pagination from '../../../components/form/Pagination';
import CallexLoader from '../../../components/CallexLoader';
import FormInput from '../../../components/form/FormInput';
import {
  checkModulePermission,
  convertToMask,
  fileDownload,
  formatDownloadDate,
  formatPayloadDate
} from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import config from '../../../config';
import { toZonedTime } from 'date-fns-tz';
import { useTimezone } from '../../../store/slices/commonSlice';

const DailyReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();

  const userRolePermission = useUserRolePermission();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [crmOptions, setCrmOptions] = useState([]);
  const [didOptions, setDidOptions] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [audioDetails, setAudioDetails] = useState(null);
  const [paginationDetails, setPaginationDetails] = useState({ page: 1, total: 0, limit: 10 });
  const [headerFields, setHeaderFields] = useState({
    default: [],
    crm_fields: []
  });

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    page: 1,
    limit: 10,
    crm_id: null,
    destination_number: '',
    did_number: '',
    process_id: [],
    type: 'all',
    call_type: 'all',
    start_date: startOfDay(today),
    end_date: endOfDay(today)
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required'),
    crm_id: Yup.number().required('CRM is required'),
    process_id: Yup.array().min(1, 'Process is required').required('Process is required'),
    type: Yup.string().required('Type is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        destination_number: values?.destination_number || undefined,
        did_number: values?.did_number || undefined
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;
      payload['process_id'] = values?.process_id?.join(',');

      const response = await dispatch(dailyReportGet(payload)).unwrap();
      if (response) {
        setHeaderFields({
          ...headerFields,
          default: response?.header_fields?.default || [],
          crm_fields: response?.header_fields?.crm_fields || []
        });
        setReportData(response?.data || []);
        setPaginationDetails({
          ...paginationDetails,
          limit: response.limit,
          page: response.page,
          total: response.total
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    if (values?.process_id.length > 0) {
      onSubmit();
    }
  }, [values?.limit, values?.page]);

  const getCrmsOption = async () => {
    try {
      const response = await dispatch(crmsOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setCrmOptions(options);
      } else {
        setCrmOptions([]);
      }
    } catch (error) {
      setCrmOptions([]);
      console.log('error', error);
    }
  };

  const getProcessOption = async (id) => {
    try {
      const response = await dispatch(processOptionGet({ crm_id: id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getDidOption = async () => {
    try {
      const response = await dispatch(didOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.number || '-',
          value: String(val.id)
        }));
        setDidOptions(options);
      } else {
        setDidOptions([]);
      }
    } catch (error) {
      setDidOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getDidOption();
    getCrmsOption();
  }, []);

  useEffect(() => {
    getProcessOption(values.crm_id);
  }, [values.crm_id]);

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return {
      headers: csvHeaderData,
      data,
      filename: `Daily_report_${formatDownloadDate(values.start_date)}.csv`
    };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        process_id: values.process_id.join(','),
        crm_id: values.crm_id,
        type: values.type
      };

      const response = await dispatch(dailyReportGet(payload)).unwrap();

      if (response) {
        const combinedHeaders = [
          ...(response?.header_fields?.default || []),
          ...(response?.header_fields?.crm_fields || [])
        ];

        const headers = combinedHeaders.map((val) => ({ label: val?.label, key: val?.name }));

        setCsvReportData(response?.data || []);
        setCsvHeaderData(headers);
        setIsExporting(false);
        setIsCsvDownload(true);
      }
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="Daily Report"
        isSearch
        isExportDisabled={!checkModulePermission('is_export', 'Daily Report', userRolePermission)}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={values?.crm_id && values?.process_id?.length > 0 && reportData?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  maxDate={today}
                  minDate={today}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  showTimeSelectOnly={true}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  maxDate={today}
                  minDate={today}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  showTimeSelectOnly={true}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormInput
                  label="Destination Number"
                  placeholder="Enter Destination Number"
                  value={values.destination_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="destination_number"
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="CRM"
                  placeholder="Select CRM"
                  value={values.crm_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="crm_id"
                  options={crmOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.process_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="process_id"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Type"
                  placeholder="Select Type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="type"
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Answered', value: 'answered' },
                    { label: 'Unanswered', value: 'not_answered' }
                  ]}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Call Type"
                  placeholder="Select Call Type"
                  value={values.call_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="call_type"
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'Manual', value: 'manual' },
                    { label: 'Preview', value: 'preview' },
                    { label: 'Predictive', value: 'predictive' },
                    { label: 'Progressive', value: 'progressive' },
                    { label: 'Incoming', value: 'incoming' },
                    { label: 'Voice Broadcast', value: 'voicebroadcast' }
                  ]}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="did_number"
                  label="DID Number"
                  placeholder="Select DID Number"
                  value={values.did_number}
                  options={didOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 450px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.default?.length}>Default</th>
                </tr>
                <tr>
                  {headerFields?.default?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.default?.map((value, idx) => {
                        if (value?.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center cursor-pointer">
                              {val[value?.name] ? (
                                <div className="d-flex gap-2">
                                  <Icon.PlayCircle
                                    size={16}
                                    onClick={() =>
                                      setAudioDetails({
                                        ...val,
                                        src: `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      })
                                    }
                                    className="cursor-pointer text-info"
                                  />
                                  <Icon.Download
                                    size={16}
                                    onClick={() => {
                                      fileDownload(
                                        `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        if (value?.name === 'crm_number') {
                          return (
                            <td key={idx}>
                              {process.env.REACT_APP_GLOBAL_NUMBER_MASKING === 'true'
                                ? convertToMask(val[value?.name] || '-')
                                : val[value?.name] || '-'}
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="report-list-table">
              <thead>
                <tr className="title">
                  <th colSpan={headerFields?.crm_fields?.length}>CRM</th>
                </tr>
                <tr>
                  {headerFields?.crm_fields?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {headerFields?.crm_fields?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={paginationDetails?.total}
              limit={paginationDetails?.limit}
              page={paginationDetails?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </div>
  );
};

export default DailyReport;
